import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { toast } from "react-toastify";
// Constants
import { pathHomePage, WEB_SERVICE_MIMS, applications } from "../../../config/constants";
// Components
import { CommitmentQuestionAligner, CommitmentQuestionTitle, CommitmentQuestionContainer, NoAssignedQuestionsText } from "./styles";
import Axios from "../../../config/axios";
import Loader from "../../loader";
import PatientNavigation from "../../patientNavigation";
import SoftwareMenu from "../../softwareMenu";
import QuestionnairesMenu from "../../questionnaires/questionnairesMenu";
import Title from "../../title";
import QuestionnaireInfo from "./sections/questionnaireInfo";
import ActionsManagement from "./sections/actionsManagement";
import CommitmentQuestion from "./sections/commitmentQuestion";

const CommitmentQuestions = () => {
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [isLoadingPatientQuestions, setIsLoadingPatientQuestions] = useState(true);
  const [loadingPatientQuestionsOrdering, setLoadingPatientQuestionsOrdering] = useState(true);
  const [isDisabledInputs, setIsDisabledInputs] = useState(true);

  const [commitmentQuestions, setCommitmentQuestions] = useState([]);
  const [patientCommitmentQuestions, setPatientCommitmentQuestions] = useState([]);
  const [iterablePatientCommitmentQuestions, setIterablePatientCommitmentQuestions] = useState([]);
  const [unassignedCommitmentQuestions, setUnassignedCommitmentQuestions] = useState([]);

  const { clinicianToken, patientId, software } = useParams();
  const [t] = useTranslation("global");

  // Fetch questions
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/commitment-questions/get-questions`)
      .then(({ data }) => {
        setCommitmentQuestions(data);
        setIsLoadingQuestions(false);
      })
      .catch((error) => {
        console.log("Endpoint to get commitment questions: ", error);
      });
  }, []);

  // Fetch patient questions
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/commitment-questions/get-patient-questions/${patientId}/${applications[software].id}`)
      .then(({ data }) => {
        setPatientCommitmentQuestions(data);
        setIterablePatientCommitmentQuestions(data);
        setIsLoadingPatientQuestions(false);
      })
      .catch((error) => {
        console.log("Endpoint to get patient commitment questions: ", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, software]);

  // Set unassigned commitment questions
  useEffect(() => {
    const unassignedCommitmentQuestions = commitmentQuestions.filter(
      (commitmentQuestion) =>
        !iterablePatientCommitmentQuestions.some(
          (iterablePatientCommitmentQuestion) =>
            commitmentQuestion.COMMITMENT_QUESTION_ID === iterablePatientCommitmentQuestion.COMMITMENT_QUESTION_ID
        )
    );
    setUnassignedCommitmentQuestions(unassignedCommitmentQuestions);
    setLoadingPatientQuestionsOrdering(false);
  }, [commitmentQuestions, iterablePatientCommitmentQuestions]);

  const setInitialState = () => {
    setIterablePatientCommitmentQuestions(patientCommitmentQuestions);
    setIsDisabledInputs(true);
  };

  const savePatientCommitmentQuestions = () => {
    Axios(
      "post",
      `${WEB_SERVICE_MIMS}/commitment-questions/add-patient-questions/${patientId}/${applications[software].id}`,
      iterablePatientCommitmentQuestions
    )
      .then(({ data }) => {
        if (data.patientCommitmentQuestionsAdded) {
          setIsDisabledInputs(true);
          toast.success(t("questionnaires_alerts_addedQuestions"));
        }
      })
      .catch((error) => {
        console.log("Endpoint to store patient commitment questions: ", error);
      });
  };

  const renderCommitmentQuestions = () => {
    const handleDragEnd = (event) => {
      const { activatorEvent, active, over } = event;

      const assignedQuestionsContainer = document.getElementById("assignedQuestions");
      const unassignedQuestionsContainer = document.getElementById("unassignedQuestions");
      const element = activatorEvent.target;
      const assignedQuestionsContainerPosition = assignedQuestionsContainer.getBoundingClientRect();
      const unassignedQuestionsContainerPosition = unassignedQuestionsContainer.getBoundingClientRect();
      const currentQuestion = element.getBoundingClientRect();

      const isTouchingAssignedQuestions = () => {
        return (
          currentQuestion.top >= assignedQuestionsContainerPosition.top &&
          currentQuestion.bottom <= assignedQuestionsContainerPosition.bottom &&
          currentQuestion.left >= assignedQuestionsContainerPosition.left &&
          currentQuestion.right <= assignedQuestionsContainerPosition.right
        );
      };

      const isTouchingUnassignedQuestions = () => {
        return (
          currentQuestion.top >= unassignedQuestionsContainerPosition.top &&
          currentQuestion.bottom <= unassignedQuestionsContainerPosition.bottom &&
          currentQuestion.left >= unassignedQuestionsContainerPosition.left &&
          currentQuestion.right <= unassignedQuestionsContainerPosition.right
        );
      };

      const oldQuestionIndex = iterablePatientCommitmentQuestions.findIndex((question) => question.COMMITMENT_QUESTION_ID === active.id);
      const newQuestionIndex = iterablePatientCommitmentQuestions.findIndex((question) => question.COMMITMENT_QUESTION_ID === over.id);
      const newUnassignedQuestionIndex = unassignedCommitmentQuestions.findIndex((question) => question.COMMITMENT_QUESTION_ID === over.id);

      // Move a question position in the Patient questions
      if (oldQuestionIndex >= 0 && newQuestionIndex >= 0 && isTouchingAssignedQuestions()) {
        setIterablePatientCommitmentQuestions(arrayMove(iterablePatientCommitmentQuestions, oldQuestionIndex, newQuestionIndex));
      }
      // Add a question to the Patient questions
      if (
        (oldQuestionIndex < 0 && newQuestionIndex < 0 && isTouchingAssignedQuestions()) ||
        (oldQuestionIndex < 0 && newQuestionIndex >= 0 && isTouchingAssignedQuestions())
      ) {
        if (iterablePatientCommitmentQuestions.length === 3) return toast.error(t("questionnaires_alerts_noMoreThanThreeQuestions"));
        const questionToAdd = unassignedCommitmentQuestions.find((question) => question.COMMITMENT_QUESTION_ID === active.id);
        const newIterablePatientCommitmentQuestions = [...iterablePatientCommitmentQuestions, questionToAdd];
        setIterablePatientCommitmentQuestions(newIterablePatientCommitmentQuestions);
      }
      // Remove a question from Patient questions
      if (
        (oldQuestionIndex >= 0 && newUnassignedQuestionIndex <= 0 && isTouchingUnassignedQuestions()) ||
        (oldQuestionIndex >= 0 && newUnassignedQuestionIndex >= 0 && isTouchingUnassignedQuestions())
      ) {
        const questionToRemove = iterablePatientCommitmentQuestions[oldQuestionIndex];
        const newIterablePatientCommitmentQuestions = iterablePatientCommitmentQuestions.filter(
          (patientCommitmentQuestion) => patientCommitmentQuestion.COMMITMENT_QUESTION_ID !== questionToRemove.COMMITMENT_QUESTION_ID
        );
        return setIterablePatientCommitmentQuestions(newIterablePatientCommitmentQuestions);
      }
    };

    return (
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {/* Patient commitment questions */}
        <CommitmentQuestionAligner>
          <CommitmentQuestionTitle>{t("questionnaires_assignedQuestionsTitle")}</CommitmentQuestionTitle>
          <CommitmentQuestionContainer id="assignedQuestions">
            <SortableContext items={iterablePatientCommitmentQuestions} strategy={verticalListSortingStrategy} disabled={isDisabledInputs}>
              {!iterablePatientCommitmentQuestions.length && (
                <NoAssignedQuestionsText>{t("questionnaires_assignedQuestions")}</NoAssignedQuestionsText>
              )}
              {iterablePatientCommitmentQuestions.map((commitmentQuestion) => {
                return (
                  <CommitmentQuestion
                    key={commitmentQuestion.COMMITMENT_QUESTION_ID}
                    commitmentQuestion={commitmentQuestion}
                    checkBackgroundColor={isDisabledInputs ? "#808080" : "#39b14a"}
                    isDisabledInputs={isDisabledInputs}
                    isPatientCommitmentQuestion
                  />
                );
              })}
            </SortableContext>
          </CommitmentQuestionContainer>
        </CommitmentQuestionAligner>
        {/* Unassigned commitment questions */}
        <CommitmentQuestionAligner>
          <CommitmentQuestionTitle>{t("questionnaires_unassignedQuestionsTitle")}</CommitmentQuestionTitle>
          <CommitmentQuestionContainer id="unassignedQuestions">
            <SortableContext items={unassignedCommitmentQuestions} strategy={verticalListSortingStrategy} disabled={isDisabledInputs}>
              {!unassignedCommitmentQuestions.length && (
                <NoAssignedQuestionsText>{t("questionnaires_unassignedQuestions")}</NoAssignedQuestionsText>
              )}
              {unassignedCommitmentQuestions.map((commitmentQuestion) => {
                return (
                  <CommitmentQuestion
                    key={commitmentQuestion.COMMITMENT_QUESTION_ID}
                    commitmentQuestion={commitmentQuestion}
                    checkBackgroundColor="#fff"
                    isDisabledInputs={isDisabledInputs}
                  />
                );
              })}
            </SortableContext>
          </CommitmentQuestionContainer>
        </CommitmentQuestionAligner>
      </DndContext>
    );
  };

  return (
    <>
      {isLoadingQuestions || isLoadingPatientQuestions || loadingPatientQuestionsOrdering ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          <SoftwareMenu
            prescriptionsPath={`${pathHomePage}/prescriptions/app/${clinicianToken}/${patientId}`}
            diagnosisPrescriptionsPath={`${pathHomePage}/diagnosis-prescriptions/${clinicianToken}/${patientId}/${software}`}
            questionnairePath={`${pathHomePage}/commitment-questions/${clinicianToken}/${patientId}/${software}`}
            messagesPath={`${pathHomePage}/messages/${clinicianToken}/${patientId}/${software}`}
          />
          <QuestionnairesMenu />
          <Title title={`RGS ${software}`} subtitle={`/ ${t("questionnaires_questionnaire")}`} marginBottom="10px" />
          <QuestionnaireInfo />
          <ActionsManagement
            handleSubmit={savePatientCommitmentQuestions}
            isDisabledInputs={isDisabledInputs}
            setIsDisabledInputs={setIsDisabledInputs}
            setInitialState={setInitialState}
          />
          {renderCommitmentQuestions()}
        </>
      )}
    </>
  );
};

export default CommitmentQuestions;
